import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { createPaperReservation, listAvailablePaperLotsPaged, listPaperReservations } from "../api/paperReservations";
import { fmtItemQty } from "../utils/salesOrderItems";
import { splitReservationLotNo } from "../utils/reservationLot";
import ReferencePickerDialog from "./ReferencePickerDialog";

export default function WorkOrderPaperReservationDialog({ workOrder, onClose, onSuccess }) {
  const [selectedLots, setSelectedLots] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [lotPickerOpen, setLotPickerOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [existingReservations, setExistingReservations] = useState([]);

  useEffect(() => {
    setSelectedLots([]);
    setQuantities({});
    setLotPickerOpen(false);
    setExistingReservations([]);

    const orderNo = workOrder?.order_no;
    if (!orderNo) return;
    let cancelled = false;
    listPaperReservations({ work_order_no: orderNo, status: "active", limit: 200 })
      .then((res) => { if (!cancelled) setExistingReservations(res?.items || []); })
      .catch(() => { if (!cancelled) setExistingReservations([]); });
    return () => { cancelled = true; };
  }, [workOrder?.id, workOrder?.order_no]);

  function lotKey(lot) {
    return `${lot.material_id}__${lot.lot_no}`;
  }

  const loadPaperLots = useCallback(({ page, search }) => listAvailablePaperLotsPaged({ material_code: "150.01.99", page, limit: 10, search }), []);

  function toggleLot(lot) {
    const key = lotKey(lot);
    const exists = selectedLots.some((item) => lotKey(item) === key);
    setSelectedLots((previous) => exists ? previous.filter((item) => lotKey(item) !== key) : [...previous, lot]);
    setQuantities((previous) => {
      if (exists) { const next = { ...previous }; delete next[key]; return next; }
      return { ...previous, [key]: previous[key] ?? String(Number(lot.on_hand_qty)) };
    });
  }

  async function handleSave() {
    if (!selectedLots.length) {
      alert("En az bir parti seçin");
      return;
    }

    const reservations = [];
    for (const lot of selectedLots) {
      const reserveQty = Number(quantities[lotKey(lot)]);
      if (!Number.isFinite(reserveQty) || reserveQty <= 0) {
        alert(`${lot.lot_no} için miktar pozitif olmalı`);
        return;
      }
      if (reserveQty > Number(lot.on_hand_qty) + 0.0001) {
        alert(`${lot.lot_no} için miktar mevcut stoktan (${lot.on_hand_qty}) fazla olamaz`);
        return;
      }
      reservations.push({ lot, qty: reserveQty });
    }

    const salesOrderId = Number(workOrder?.sales_order_id);
    const salesOrderItemId = Number(workOrder?.sales_order_item_id);
    if (
      !Number.isFinite(salesOrderId) ||
      salesOrderId <= 0 ||
      !Number.isFinite(salesOrderItemId) ||
      salesOrderItemId <= 0
    ) {
      alert("Bu iş emri bir sipariş kalemine bağlı değil");
      return;
    }

    setSaving(true);
    try {
      for (const reservation of reservations) {
        await createPaperReservation({
          materialId: reservation.lot.material_id,
          materialVariantId: reservation.lot.material_variant_id || null,
          sourceLotNo: reservation.lot.lot_no,
          qty: reservation.qty,
          salesOrderId,
          salesOrderItemId,
          note: `İş emri: ${workOrder?.order_no || workOrder?.id}`,
        });
      }
      onSuccess?.();
    } catch (e) {
      alert(e?.response?.data?.error?.message || "Kaydetme başarısız");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
    <Dialog open onClose={saving ? undefined : onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 4, overflow: "hidden", boxShadow: "0 24px 70px rgba(15, 23, 42, 0.22)" } }}>
      <DialogTitle sx={{ p: 0 }}>
        <Box sx={{ px: { xs: 2.5, sm: 3.5 }, py: 2.5, color: "#fff", background: "linear-gradient(135deg, #312e81 0%, #4f46e5 56%, #6366f1 100%)" }}>
          <Stack direction="row" alignItems="center" spacing={1.5}><Box sx={{ width: 42, height: 42, borderRadius: 2.25, display: "grid", placeItems: "center", bgcolor: "rgba(255,255,255,0.16)", border: "1px solid rgba(255,255,255,0.24)", fontSize: 19 }}>▣</Box><Box><Typography variant="h6" fontWeight={800}>İş Emrine Kağıt Rezervasyonu</Typography><Typography variant="body2" sx={{ opacity: 0.8 }}>İş emri için ayrılacak kağıt partilerini ve miktarlarını belirleyin.</Typography></Box></Stack>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: "#f7f8fc" }}>
        <Stack spacing={2}>
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Rezervasyon seçilen iş emrinin kayıtlı sipariş ve sipariş kalemine yapılacaktır.
          </Alert>

          <Paper elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, border: "1px solid #e7eaf3", bgcolor: "#fff" }}><Stack spacing={0.5}>
            <Typography variant="body2"><b>İş Emri:</b> {workOrder?.order_no || "—"}</Typography>
            <Typography variant="body2"><b>Müşteri:</b> {workOrder?.customer_name || "—"}</Typography>
            <Typography variant="body2"><b>Sipariş No:</b> {workOrder?.sales_order_no || "—"}</Typography>
          </Stack></Paper>

          <Paper elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, border: "1px solid #e7eaf3", bgcolor: "#fff" }}>
            <Box sx={{ mb: 1.5 }}><Stack direction="row" spacing={1} alignItems="center"><Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#6366f1" }} /><Typography fontWeight={800}>Kağıt partileri</Typography></Stack><Typography variant="body2" color="text.secondary">Partileri arayarak seçin; birden fazla parti aynı rezervasyona eklenebilir.</Typography></Box>
            <Button variant="outlined" onClick={() => setLotPickerOpen(true)} disabled={saving} sx={{ borderRadius: 2, textTransform: "none" }}>{selectedLots.length ? `${selectedLots.length} parti seçildi · Partileri düzenle` : "Parti seçiniz"}</Button>
          </Paper>

          {selectedLots.length ? <Paper elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, border: "1px solid #e7eaf3", bgcolor: "#fff" }}>
            <Typography fontWeight={800} sx={{ mb: 0.5 }}>Rezervasyon miktarları</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Her seçili parti için ayrılacak miktarı girin.</Typography>
            <Stack spacing={1.5}>{selectedLots.map((lot) => {
            const key = lotKey(lot);
            return (
              <TextField
                key={key}
                size="small"
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2.25 } }}
                label={`${lot.lot_no} miktarı (maks: ${fmtItemQty(lot.on_hand_qty)} ${lot.material_unit || ""})`}
                type="number"
                value={quantities[key] ?? ""}
                onChange={(e) => setQuantities((previous) => ({ ...previous, [key]: e.target.value }))}
                inputProps={{ min: 0, step: "any" }}
                fullWidth
                disabled={saving}
              />
            );
            })}</Stack>
          </Paper> : null}

          {existingReservations.length ? <Paper elevation={0} sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 3, border: "1px solid #e7eaf3", bgcolor: "#fff" }}>
            <Typography fontWeight={800} sx={{ mb: 0.5 }}>Rezerve edilmiş partiler</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>Bu iş emri için önceden ayrılmış kağıt partileri.</Typography>
            <Stack spacing={0.75}>{existingReservations.map((r) => {
              const rezNo = splitReservationLotNo(r.reserved_lot_no).reservationNo;
              return (
                <Stack key={r.id} direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ flexWrap: "wrap" }}>
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {r.source_lot_no}{rezNo ? ` · ${rezNo}` : ""}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {fmtItemQty(r.reserved_qty)} {r.material_unit || ""}
                  </Typography>
                </Stack>
              );
            })}</Stack>
          </Paper> : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3.5 }, py: 2, bgcolor: "#fff", borderTop: "1px solid #e7eaf3", justifyContent: "space-between" }}>
        <Typography variant="caption" color="text.secondary">* En az bir parti seçip her parti için rezervasyon miktarını girin.</Typography>
        <Stack direction="row" spacing={1}><Button onClick={onClose} disabled={saving} color="inherit">Kapat</Button><Button onClick={handleSave} disabled={saving || !selectedLots.length} variant="contained" sx={{ px: 2.5, borderRadius: 2, boxShadow: "0 8px 18px rgba(79, 70, 229, 0.24)" }}>{saving ? "Kaydediliyor..." : "Rezervasyonu Kaydet"}</Button></Stack>
      </DialogActions>
    </Dialog>
    <ReferencePickerDialog
      open={lotPickerOpen}
      title="Kağıt Partisi Seç"
      searchPlaceholder="Parti no, malzeme veya varyant ile ara..."
      fetchPage={loadPaperLots}
      getPrimary={(lot) => lot.lot_no}
      getSecondary={(lot) => `${lot.material_code || ""}${lot.material_name ? ` · ${lot.material_name}` : ""}${lot.variant_display_name ? ` · ${lot.variant_display_name}` : ""} · Stok: ${fmtItemQty(lot.on_hand_qty)} ${lot.material_unit || ""}`}
      multiple
      selectedKeys={selectedLots.map(lotKey)}
      itemKey={lotKey}
      onToggle={toggleLot}
      onConfirm={() => setLotPickerOpen(false)}
      onClose={() => setLotPickerOpen(false)}
    />
    </>
  );
}
